/**
 * Utility functions for handling mentions in chat messages
 */

export interface ParsedMention {
  id: string;
  name: string;
  originalText: string;
}

export interface ParsedMessage {
  displayText: string;
  mentions: ParsedMention[];
  // mentionIds: string[];
}

/**
 * Safely extracts text content from assistant-ui message content
 * Handles both string and array formats
 */
export function extractTextFromMessageContent(content: any): string {
  if (typeof content === 'string') {
    return content;
  }
  
  if (Array.isArray(content)) {
    return content
      .map(part => {
        if (part && typeof part === 'object' && part.type === 'text' && typeof part.text === 'string') {
          return part.text;
        }
        return '';
      })
      .join('');
  }
  
  return '';
}

/**
 * Parses a message containing mentions in the format @[display]{id}
 * Returns the display text with mentions simplified to @display and extracted mention data
 * Can handle both string content and array content from assistant-ui
 */
export function parseMessageWithMentions(content: string | readonly any[]): ParsedMessage {
  // Extract text content if it's an array
  const messageText = typeof content === 'string' 
    ? content
    : extractTextFromMessageContent(content);
  
  // Regex to match @[display]{id} pattern
  const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
  
  const mentions: ParsedMention[] = [];
  // const mentionIds: string[] = [];
  
  // Replace mentions with simplified @display format
  const displayText = messageText.replace(mentionRegex, (match, display, id) => {
    mentions.push({
      id,
      name : display,
      originalText: match
    });
    // mentionIds.push(id);
    return `@${display}`;
  });
  
  return {
    displayText,
    mentions,
    // mentionIds,
  };
}

/**
 * Extracts mention IDs from a message without modifying the text
 */
export function extractMentionIds(message: string): string[] {
  const mentionRegex = /@\[[^\]]+\]\{([^}]+)\}/g;
  const ids: string[] = [];
  let match;
  
  while ((match = mentionRegex.exec(message)) !== null) {
    ids.push(match[1]);
  }
  
  return ids;
}

/**
 * Checks if a message contains mentions
 */
export function hasMentions(message: string): boolean {
  return /@\[[^\]]+\]\{[^}]+\}/.test(message);
}

/**
 * Converts a message with simple @display mentions back to @[display]{id} format
 * This is useful when you need to reconstruct the original format
 */
export function convertToFullMentionFormat(
  message: string, 
  mentionMap: Map<string, string>
): string {
  return message.replace(/@([^\s@]+)/g, (match, display) => {
    const id = mentionMap.get(display);
    return id ? `@[${display}]{${id}}` : match;
  });
}
